import type { PropsWithChildren, ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { classNames } from "src/core/utilities/classNames";
import type { FrameLink } from "./useFrameMenu";
import { church } from "src/core/church/church";
import { ChurchMark } from "src/core/church/ChurchImages";

export const FrameLogo = ({ before }: { before?: ReactNode }) => (
    <div className="flex items-center gap-2 px-2 min-h-16">
        {before}

        <Link to="/" className="flex items-center gap-2.5 transition-transform active:scale-95">
            <ChurchMark className="dark:invert h-9 object-contain" />

            <span className="text-lg font-extrabold tracking-tight">{church.appName}</span>
        </Link>
    </div>
);

interface RailLinkProps {
    link: FrameLink;

    /** A ministry, listed under Ministries itself in a smaller row */
    nested?: boolean;
}

/** A full-width pill row, as the expanded rail lists them */
export const RailLink = ({ link, nested = false }: RailLinkProps) => (
    <NavLink
        to={link.to}
        end={link.end}
        aria-current={link.forceInactive ? false : undefined}
        className={({ isActive }) =>
            classNames(
                "flex items-center rounded-full transition-colors",
                nested ? "gap-3 min-h-10 pl-12 pr-4 text-[0.85rem]" : "gap-3.5 min-h-11 px-4 text-[0.9rem]",
                isActive && !link.forceInactive
                    ? "bg-indicator text-on-indicator font-bold"
                    : "font-medium text-base-content/75 hover:bg-base-content/10 hover:text-base-content"
            )
        }
    >
        <link.icon
            className={classNames(nested ? "w-[1.1rem] h-[1.1rem]" : "w-[1.35rem] h-[1.35rem]", "shrink-0", {
                "opacity-50": link.faded,
            })}
        />

        <span className="leading-tight">{link.label}</span>
    </NavLink>
);

/** The same size as the rows it heads, so a section reads as a group rather than a footnote */
export const FrameHeading = ({ children, className }: PropsWithChildren<{ className?: string }>) => (
    <h3 className={classNames("px-4 pt-2 pb-1 text-sm font-semibold text-base-content/60", className)}>{children}</h3>
);

/** Rows in their own containers, rounded at the ends of the group, as the More sheet lists them */
export const SheetList = ({ children }: PropsWithChildren) => (
    <ul className="grid gap-0.5 [&>li>*]:rounded-md [&>li:first-child>*]:rounded-t-2xl [&>li:last-child>*]:rounded-b-2xl">
        {children}
    </ul>
);

export const sheetRowClass = "flex items-center gap-4 w-full min-h-14 px-4 py-2 bg-base-100 text-left font-semibold";

export const SheetLink = ({ link }: { link: FrameLink }) => (
    <li>
        <NavLink
            to={link.to}
            end={link.end}
            aria-current={link.forceInactive ? false : undefined}
            className={({ isActive }) =>
                classNames(sheetRowClass, "transition-colors", {
                    "!bg-indicator text-on-indicator": isActive && !link.forceInactive,
                })
            }
        >
            {({ isActive }) => (
                <>
                    <span
                        className={classNames(
                            "grid place-items-center w-9 h-9 rounded-full shrink-0",
                            isActive && !link.forceInactive ? "bg-primary text-primary-content" : "bg-indicator text-on-indicator"
                        )}
                    >
                        <link.icon className={classNames("w-5 h-5", { "opacity-50": link.faded })} />
                    </span>

                    {link.label}
                </>
            )}
        </NavLink>
    </li>
);
